import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { getSuperAdminSchoolClasses, SAClassRow } from '../../services/api';
import SuperAdminLayout from './SuperAdminLayout';

const Ubar: React.FC<{ got: number; almost: number; needs: number; none: number }> = ({ got, almost, needs, none }) => {
  const total = got + almost + needs + none || 1;
  const w = (n: number) => `${(n / total) * 100}%`;
  return (
    <span className="ubar">
      <i className="u-got" style={{ width: w(got) }} />
      <i className="u-part" style={{ width: w(almost) }} />
      <i className="u-not" style={{ width: w(needs) }} />
      <i className="u-exp" style={{ width: w(none) }} />
    </span>
  );
};

/** Task 147 — classes in a school with the understanding mix (artifact styling). */
const SuperAdminSchoolClasses: React.FC = () => {
  const navigate = useNavigate();
  const { school: schoolParam } = useParams<{ school: string }>();
  const school = schoolParam ? decodeURIComponent(schoolParam) : '';
  const [rows, setRows] = useState<SAClassRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const token = localStorage.getItem('superAdminToken');
    if (!token) {
      navigate('/super-admin/login', { replace: true });
      return;
    }
    getSuperAdminSchoolClasses(token, school)
      .then(setRows)
      .catch((e) => {
        const msg = e instanceof Error ? e.message : 'Failed to load.';
        setError(msg);
        if (/token|unauth/i.test(msg)) {
          localStorage.removeItem('superAdminToken');
          navigate('/super-admin/login', { replace: true });
        }
      })
      .finally(() => setLoading(false));
  }, [navigate, school]);

  const openClass = (c: SAClassRow) => {
    const params = new URLSearchParams({ school, grade: String(c.grade) });
    if (c.section) params.set('section', c.section);
    navigate(`/super-admin/class?${params.toString()}`);
  };

  return (
    <SuperAdminLayout>
      <div className="content">
        <div className="back" onClick={() => navigate('/super-admin/schools')}>‹ All schools</div>
        <h1 className="pg">{school}</h1>
        <p className="pg-sub">Classes and how the children are doing. Click a class to see each student.</p>

        {loading ? (
          <p className="hint">Loading…</p>
        ) : error ? (
          <p className="hint" style={{ color: 'var(--crit)' }}>{error}</p>
        ) : rows.length === 0 ? (
          <p className="hint">No classes yet.</p>
        ) : (
          <>
            <div className="tblwrap">
              <table>
                <thead>
                  <tr>
                    <th>Class</th>
                    <th className="num">Students</th>
                    <th className="num">Active</th>
                    <th>Understanding <span className="ai">AI</span></th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((c) => (
                    <tr key={c.label} className="clk" onClick={() => openClass(c)}>
                      <td className="who">{c.label}</td>
                      <td className="num">{c.students}</td>
                      <td className="num">{c.active_week}</td>
                      <td><Ubar got={c.got} almost={c.almost} needs={c.needs} none={c.no_data} /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="ukey">
              <span><i className="u-got" />Got it</span>
              <span><i className="u-part" />Almost</span>
              <span><i className="u-not" />Needs practice</span>
              <span><i className="u-exp" />No data</span>
            </div>
          </>
        )}
      </div>
    </SuperAdminLayout>
  );
};

export default SuperAdminSchoolClasses;
